import React, { useState, useEffect } from 'react';
import {
  Mail,
  Send,
  CheckCircle2,
  BatteryCharging,
  Cpu,
  Zap,
  Plug,
  Cog,
  Thermometer,
  ExternalLink,
  ArrowLeft,
  Handshake,
  MapPin,
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import SEO from './SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const CONTACT_EMAIL = 'info@e-musclecars.com';

// Hardcoded component categories mirroring what E-Muscle Cars stocked on the
// old storefront. Each category shows both a summary and a small list of
// specific components — enough to be concrete without dragging in SKUs or
// pricing that need constant maintenance. `image` is the banner photo shown
// at the top of the card; sourced from the original storefront's category
// artwork (studio-lit real EV components on a garage workbench).
const COMPONENT_CATEGORIES = [
  {
    icon: BatteryCharging,
    title: 'Battery Packs',
    image: '/e-musclecar/cat/batteries.png',
    body:
      '400V and 800V pack options in a range of capacities. Structural enclosures with integrated BMS, thermal plates, and pre-terminated harnesses — ready to drop into a validated chassis kit.',
    items: [
      '400V structural packs (32–100 kWh)',
      '800V high-voltage systems',
      'Platform-specific enclosures (Mustang, Camaro, Cobra)',
      'Integrated BMS + pre-terminated HV harnesses',
    ],
  },
  {
    icon: Cpu,
    title: 'Motors & Drive Units',
    image: '/e-musclecar/cat/motors.png',
    body:
      'Proven drive-unit options matched to the horsepower and torque targets of your platform — from budget-friendly single-motor builds to dual-motor performance setups.',
    items: [
      'Tesla LDU (Large Drive Unit) conversion package',
      'Tesla SDU (Small Drive Unit) conversion package',
      'Cascadia / BorgWarner iM225DX crate units',
      'Netgain HyPer 9 / AC-51 for budget builds',
    ],
  },
  {
    icon: Cog,
    title: 'BMS & Controls',
    image: '/e-musclecar/cat/wiring.png',
    body:
      'Everything that keeps the high-voltage side safe and the driver informed. Documented wiring, labeled looms, and serviceable connectors throughout.',
    items: [
      'Orion 2 & Elithion battery management',
      'Vehicle control units (VCU) + throttle mapping',
      'HV contactors, precharge & fuse assemblies',
      'Digital instrumentation & telemetry displays',
    ],
  },
  {
    icon: Plug,
    title: 'Chargers & DC-DC',
    image: '/e-musclecar/cat/packages.png',
    body:
      'On-board chargers, DC-DC converters, and charge-port integrations engineered to fit within classic bodywork without cutting up your fender.',
    items: [
      'On-board chargers (3.3 kW – 22 kW)',
      'J1772 & CCS charge-port assemblies',
      'DC-DC converters for the 12V system',
      'Hidden charge-port integration kits',
    ],
  },
  {
    icon: Zap,
    title: 'Inverters',
    image: '/e-musclecar/cat/brakes.png',
    body:
      'High-voltage inverters paired with matched motors. Configurable for the power, torque, and drivability profile you want in the finished car.',
    items: [
      'Cascadia PM100 / PM250 series',
      'Prohelion / Rinehart PM inverters',
      'Tesla-integrated inverter (LDU/SDU packages)',
      'Custom tuning & torque-vectoring configs',
    ],
  },
  {
    icon: Thermometer,
    title: 'Cooling & Thermal',
    image: '/e-musclecar/cat/hvac.png',
    body:
      'Thermal-management hardware validated for the pack / drive-unit combinations we recommend. Runs cool at the drag strip and on a hot Texas afternoon.',
    items: [
      'Battery cooling plates & cold plates',
      'Motor & inverter coolant loops',
      'Compact radiators + electric water pumps',
      'Cabin HVAC electrification (heat pump / PTC)',
    ],
  },
];

// Reusable photo-background block. Renders a section-fill image with a
// dark gradient overlay so text remains readable. Same treatment used on
// emc-pms.com — feels atmospheric without pulling focus from the copy.
const PhotoBackdrop = ({ src, position = 'center', opacity = 0.28, gradient }) => (
  <>
    <div
      className="pointer-events-none absolute inset-0 bg-cover"
      style={{
        backgroundImage: `url('${src}')`,
        backgroundPosition: position,
        opacity,
      }}
      aria-hidden="true"
    />
    <div
      className={`pointer-events-none absolute inset-0 ${
        gradient || 'bg-gradient-to-b from-neutral-950/70 via-neutral-950/85 to-neutral-950'
      }`}
      aria-hidden="true"
    />
  </>
);

const EMuscleCarComponents = () => {
  const [partners, setPartners] = useState([]);
  const [contactOpen, setContactOpen] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', company: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const location = useLocation();

  useEffect(() => {
    axios
      .get(`${API}/content/converter-partners`)
      .then((res) => {
        const visible = (res.data || [])
          .filter((p) => p.visible !== false)
          .sort((a, b) => (a.order || 0) - (b.order || 0));
        setPartners(visible);
      })
      .catch(() => setPartners([]));
  }, []);

  // Support deep-link anchors from the /e-musclecars landing page CTAs.
  // React Router doesn't scroll to fragment IDs automatically on client-side
  // navigation, so we do it here once the DOM has rendered.
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    // small delay so the section is definitely mounted
    const t = setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
    return () => clearTimeout(t);
  }, [location.hash, partners.length]);

  const openContact = (topic) => {
    setForm({
      name: '',
      email: '',
      phone: '',
      company: '',
      message: topic ? `Interested in: ${topic}\n\n` : '',
    });
    setSubmitted(false);
    setContactOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const wrappedMessage = `[E-Muscle Car inquiry — please forward to ${CONTACT_EMAIL}]\n\n${form.message.trim()}`;
    try {
      await axios.post(`${API}/contact/submit`, { ...form, message: wrappedMessage, source: 'e-musclecars' });
      setSubmitted(true);
      toast.success("Message sent — we'll be in touch soon.");
      setTimeout(() => setContactOpen(false), 1800);
    } catch (err) {
      toast.error('Could not send message. Please email us directly instead.');
    } finally {
      setSubmitting(false);
    }
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://shireadvisors.com' },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'E-Muscle Car',
            item: 'https://shireadvisors.com/e-musclecars',
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: 'Components & Consulting',
            item: 'https://shireadvisors.com/e-musclecars/components-consulting',
          },
        ],
      },
      {
        '@type': 'CollectionPage',
        '@id': 'https://shireadvisors.com/e-musclecars/components-consulting',
        url: 'https://shireadvisors.com/e-musclecars/components-consulting',
        name: 'E-Muscle Car — Components, Consulting & Converter Partners',
        description:
          'Component categories (batteries, motors, BMS, chargers, inverters, thermal), consulting services, and recommended EV conversion partner shops from E-Muscle Cars.',
        inLanguage: 'en-US',
        isPartOf: {
          '@type': 'WebSite',
          url: 'https://shireadvisors.com',
          name: 'Shire Advisors',
        },
      },
    ],
  };

  return (
    <div className="bg-neutral-950 text-neutral-100" data-testid="emuscle-details-page">
      <SEO
        title="Components, Consulting & Converter Partners"
        description="E-Muscle Cars offers batteries, motors, BMS, chargers, inverters, and thermal components — plus consulting for EV conversion builders. Recommended converter partners: EV West, Legacy EV, Electric GT."
        path="/e-musclecars/components-consulting"
        jsonLd={jsonLd}
      />

      {/* Header strip / back link */}
      <div className="border-b border-neutral-900">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 py-6 flex items-center justify-between">
          <Link
            to="/e-musclecars"
            className="inline-flex items-center gap-2 text-sm text-neutral-400 hover:text-amber-400 transition-colors"
            data-testid="emuscle-details-back"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to E-Muscle Car
          </Link>
          <span className="text-xs tracking-[0.25em] uppercase text-amber-400 font-semibold">
            Components · Consulting · Partners
          </span>
        </div>
      </div>

      {/* Page hero */}
      <section className="relative pt-24 sm:pt-32 pb-16 overflow-hidden">
        <PhotoBackdrop
          src="/e-musclecar/bg-motor-battery.png"
          position="center right"
          opacity={0.55}
          gradient="bg-gradient-to-r from-neutral-950 via-neutral-950/85 to-neutral-950/20"
        />
        <div className="relative max-w-4xl mx-auto px-6 lg:px-8">
          <h1 className="text-4xl sm:text-5xl font-serif font-bold leading-tight mb-4">
            Components, consulting, <span className="text-amber-400">and friends in the trade.</span>
          </h1>
          <p className="text-lg text-neutral-300 leading-relaxed max-w-3xl">
            The catalog we spec&apos;d, the expertise behind it, and the shops we trust to
            take on the work we&apos;re no longer doing in-house.
          </p>
        </div>
      </section>

      {/* Components section */}
      <section id="components" className="relative py-16 sm:py-20 border-t border-neutral-900 overflow-hidden">
        <PhotoBackdrop
          src="/e-musclecar/bg-motor-battery.png"
          position="center left"
          opacity={0.18}
          gradient="bg-gradient-to-l from-neutral-950 via-neutral-950/90 to-neutral-950/70"
        />
        <div className="relative max-w-6xl mx-auto px-6 lg:px-8">
          <div className="mb-12 max-w-3xl">
            <span className="inline-block text-amber-400 font-semibold text-xs tracking-[0.25em] uppercase mb-3">
              Components
            </span>
            <h2 className="text-3xl sm:text-4xl font-serif font-bold mb-4 leading-tight">
              The catalog we built classics with.
            </h2>
            <p className="text-neutral-300 leading-relaxed">
              We source, validate, and stock the same parts that went into E-Muscle Car
              conversions — grouped here by category. Availability and pricing vary; reach
              out with the platform and target specs and we&apos;ll put a shortlist together.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {COMPONENT_CATEGORIES.map(({ icon: Icon, title, body, items, image }) => (
              <article
                key={title}
                className="relative group bg-neutral-900/60 border border-neutral-800 rounded-xl overflow-hidden transition-all hover:border-amber-500/60 hover:-translate-y-1 flex flex-col"
                data-testid={`emuscle-component-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              >
                {/* Banner photo — real EV component studio shot */}
                {image && (
                  <div className="relative h-44 overflow-hidden bg-neutral-950">
                    <img
                      src={image}
                      alt={`${title} — E-Muscle Cars components`}
                      loading="lazy"
                      decoding="async"
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div
                      className="absolute inset-0 bg-gradient-to-b from-neutral-950/10 via-neutral-950/20 to-neutral-950"
                      aria-hidden="true"
                    />
                    {/* Icon overlay */}
                    <div className="absolute bottom-3 left-4 w-11 h-11 rounded-lg bg-neutral-950/80 border border-amber-500/40 backdrop-blur flex items-center justify-center">
                      <Icon className="w-5 h-5 text-amber-400" />
                    </div>
                  </div>
                )}
                <div className="p-6 flex flex-col flex-1">
                  <h3 className="text-xl font-serif font-bold mb-2 text-white">{title}</h3>
                  <p className="text-sm text-neutral-400 leading-relaxed mb-4">{body}</p>
                  {items && items.length > 0 && (
                    <ul className="text-sm text-neutral-300 space-y-1.5 mb-5 flex-1">
                      {items.map((it) => (
                        <li key={it} className="flex items-start gap-2">
                          <span className="text-amber-500/70 mt-0.5">›</span>
                          <span>{it}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  <button
                    type="button"
                    onClick={() => openContact(title)}
                    className="text-sm text-amber-400 hover:text-amber-300 font-semibold inline-flex items-center gap-1 mt-auto"
                    data-testid={`emuscle-component-cta-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                  >
                    Inquire →
                  </button>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Consulting deep dive */}
      <section id="consulting" className="relative py-16 sm:py-20 border-t border-neutral-900 bg-neutral-900/30 overflow-hidden">
        <PhotoBackdrop
          src="/e-musclecar/custom-conversion.png"
          position="center"
          opacity={0.22}
          gradient="bg-gradient-to-b from-neutral-950/90 via-neutral-950/85 to-neutral-950/95"
        />
        <div className="relative max-w-4xl mx-auto px-6 lg:px-8">
          <span className="inline-block text-amber-400 font-semibold text-xs tracking-[0.25em] uppercase mb-3">
            Consulting
          </span>
          <h2 className="text-3xl sm:text-4xl font-serif font-bold mb-6 leading-tight">
            A decade of platform work, on tap.
          </h2>

          <div className="space-y-5 text-neutral-300 text-lg leading-relaxed mb-10">
            <p>
              An EV conversion isn&apos;t just an engine swap. It&apos;s a re-engineering
              of the entire powertrain — mass distribution, thermal management, high-voltage
              safety, braking, charging, HVAC, dashboard telemetry — usually inside a body
              that was never designed for any of it. Most projects don&apos;t fail because
              of the motor. They fail because of a hundred small decisions upstream.
            </p>
            <p>
              Our consulting is where we sit down with you, look at the platform,
              understand the goals (daily driver? weekend hero? track car?), and work
              through the decision tree before you spend money you can&apos;t easily get
              back. We&apos;ve done that mental exercise across Mustangs, Camaros,
              Corvettes, Cobras, Broncos, Porsches, Land Rovers, C-10s, VW Buses, and
              classic exotics — and we&apos;ll bring what we&apos;ve learned to your
              build.
            </p>
            <p>
              Engagements range from a single one-hour phone consult to multi-week paid
              reviews with drawings, spec documents, and vendor recommendations. Common
              deliverables include:
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 mb-10">
            {[
              {
                title: 'Feasibility & Platform Review',
                body:
                  'Is this chassis a good candidate? Where are the gotchas? Realistic budget and timeline.',
              },
              {
                title: 'Drivetrain & Battery Specification',
                body:
                  'Motor selection, pack sizing, packaging study, range/performance modeling.',
              },
              {
                title: 'Thermal & Charging Design Review',
                body:
                  'Cooling loops, radiator sizing, charge port placement, J1772 vs CCS trade-offs.',
              },
              {
                title: 'Build QA & Documentation',
                body:
                  'Wiring diagram review, harness labeling standards, hand-off documentation.',
              },
            ].map((c) => (
              <div
                key={c.title}
                className="border border-neutral-800 rounded-lg p-5 bg-neutral-950"
              >
                <h4 className="text-white font-semibold mb-1">{c.title}</h4>
                <p className="text-sm text-neutral-400 leading-relaxed">{c.body}</p>
              </div>
            ))}
          </div>

          <p className="text-neutral-400 text-sm leading-relaxed mb-6">
            Rates are quoted per engagement based on scope — reach out with a description
            of the build and we&apos;ll respond with a proposal, not a boilerplate.
          </p>

          <Button
            onClick={() => openContact('Consulting engagement')}
            size="lg"
            className="bg-amber-500 hover:bg-amber-400 text-neutral-950 font-semibold"
            data-testid="emuscle-consulting-cta-2"
          >
            <Mail className="w-4 h-4 mr-2" />
            Start a Consulting Conversation
          </Button>
        </div>
      </section>

      {/* Converter partners */}
      <section id="partners" className="relative py-16 sm:py-20 border-t border-neutral-900 overflow-hidden">
        <PhotoBackdrop
          src="/e-musclecar/hero.jpg"
          position="center"
          opacity={0.18}
          gradient="bg-gradient-to-b from-neutral-950 via-neutral-950/85 to-neutral-950"
        />
        <div className="relative max-w-6xl mx-auto px-6 lg:px-8">
          <div className="mb-10 max-w-3xl">
            <span className="inline-block text-amber-400 font-semibold text-xs tracking-[0.25em] uppercase mb-3">
              Converter Partners
            </span>
            <h2 className="text-3xl sm:text-4xl font-serif font-bold mb-4 leading-tight">
              Shops we trust with the work we&apos;re no longer doing.
            </h2>
            <p className="text-neutral-300 leading-relaxed">
              If you&apos;re after a full end-to-end conversion, these are the shops
              we&apos;d send our own friends to. Different specialties, different regions
              — reach out to them directly.
            </p>
          </div>

          {partners.length === 0 ? (
            <div className="text-center text-neutral-500 py-8 text-sm">
              No partners currently listed.
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {partners.map((p) => (
                <article
                  key={p.id}
                  className="relative group bg-neutral-900/50 border border-neutral-800 rounded-xl p-6 transition-all hover:border-amber-500/60 hover:-translate-y-1 flex flex-col"
                  data-testid={`emuscle-partner-${p.id}`}
                >
                  <div className="absolute top-0 left-6 right-6 h-0.5 bg-gradient-to-r from-transparent via-amber-500 to-transparent opacity-50 group-hover:opacity-100 transition-opacity" />
                  <div className="w-11 h-11 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center mb-4">
                    <Handshake className="w-5 h-5 text-amber-400" />
                  </div>
                  <h3 className="text-xl font-serif font-bold mb-1 text-white">{p.name}</h3>
                  {p.location && (
                    <div className="flex items-center gap-1.5 text-xs text-neutral-500 mb-3">
                      <MapPin className="w-3.5 h-3.5" />
                      <span>{p.location}</span>
                    </div>
                  )}
                  {p.description && (
                    <p className="text-sm text-neutral-400 leading-relaxed mb-4 flex-1">
                      {p.description}
                    </p>
                  )}
                  {p.url && (
                    <a
                      href={p.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-sm font-semibold text-amber-400 hover:text-amber-300 transition-colors mt-auto"
                      data-testid={`emuscle-partner-link-${p.id}`}
                    >
                      Visit {p.name}
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  )}
                </article>
              ))}
            </div>
          )}

          <p className="mt-8 text-xs text-neutral-500 max-w-3xl leading-relaxed">
            Recommendations only — these are independent businesses. E-Muscle Cars has no
            financial arrangement with any listed shop; we simply respect their work.
          </p>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="py-16 border-t border-neutral-900 bg-neutral-900/40">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-serif font-bold mb-4">
            Ready to talk components or a build?
          </h2>
          <div className="flex flex-col items-center gap-3">
            <Button
              onClick={() => openContact('')}
              size="lg"
              className="bg-amber-500 hover:bg-amber-400 text-neutral-950 font-semibold"
              data-testid="emuscle-details-closing-cta"
            >
              <Mail className="w-4 h-4 mr-2" />
              Contact E-Muscle Cars
            </Button>
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="text-sm text-neutral-400 hover:text-amber-400 transition-colors inline-flex items-center gap-1.5"
              data-testid="emuscle-details-mailto"
            >
              or email us directly at {CONTACT_EMAIL}
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>
      </section>

      {/* Contact Modal — same behavior as the landing page */}
      <Dialog open={contactOpen} onOpenChange={setContactOpen}>
        <DialogContent
          className="max-w-lg w-[95vw] p-0 overflow-hidden bg-neutral-950 border-neutral-800 text-neutral-100"
          data-testid="emuscle-details-contact-modal"
        >
          <DialogHeader className="px-6 pt-5 pb-3 border-b border-neutral-800 bg-neutral-900">
            <DialogTitle className="text-white font-serif text-xl pr-8">
              Contact E-Muscle Cars
            </DialogTitle>
            <DialogDescription className="text-amber-400 text-sm">
              Component & consulting inquiries
            </DialogDescription>
          </DialogHeader>

          {submitted ? (
            <div className="p-8 text-center" data-testid="emuscle-details-contact-success">
              <div className="w-14 h-14 rounded-full bg-amber-500/10 border border-amber-500/40 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-amber-400" />
              </div>
              <h3 className="text-lg font-serif font-bold text-white mb-1">Message Sent</h3>
              <p className="text-sm text-neutral-400">
                Thanks — we&apos;ll get back to you shortly.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="p-6 space-y-3">
              <div className="grid sm:grid-cols-2 gap-3">
                <Input
                  required
                  placeholder="Your name *"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="bg-neutral-900 border-neutral-700 text-white placeholder:text-neutral-500"
                  data-testid="emuscle-details-form-name"
                />
                <Input
                  required
                  type="email"
                  placeholder="Email *"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="bg-neutral-900 border-neutral-700 text-white placeholder:text-neutral-500"
                  data-testid="emuscle-details-form-email"
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <Input
                  type="tel"
                  placeholder="Phone (optional)"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="bg-neutral-900 border-neutral-700 text-white placeholder:text-neutral-500"
                  data-testid="emuscle-details-form-phone"
                />
                <Input
                  placeholder="Company / Shop (optional)"
                  value={form.company}
                  onChange={(e) => setForm({ ...form, company: e.target.value })}
                  className="bg-neutral-900 border-neutral-700 text-white placeholder:text-neutral-500"
                  data-testid="emuscle-details-form-company"
                />
              </div>
              <Textarea
                required
                rows={5}
                placeholder="Platform, component you're after, or the consulting question you have."
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="bg-neutral-900 border-neutral-700 text-white placeholder:text-neutral-500"
                data-testid="emuscle-details-form-message"
              />
              <p className="text-[11px] text-neutral-500 leading-relaxed">
                Messages route through Shire Advisors and are forwarded to {CONTACT_EMAIL}.
              </p>
              <div className="flex gap-2 pt-1">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1 bg-transparent border-neutral-700 text-neutral-300 hover:bg-neutral-800 hover:text-white"
                  onClick={() => setContactOpen(false)}
                  disabled={submitting}
                  data-testid="emuscle-details-form-cancel"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-amber-500 hover:bg-amber-400 text-neutral-950 font-semibold"
                  disabled={submitting}
                  data-testid="emuscle-details-form-submit"
                >
                  {submitting ? (
                    'Sending…'
                  ) : (
                    <>
                      <Send className="w-3.5 h-3.5 mr-1.5" />
                      Send Message
                    </>
                  )}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EMuscleCarComponents;
